import { Component, effect, Inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { IGolfSourceService } from '../../../services/IGolfCourse/igolf-source.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { Router } from '@angular/router';
import { ToasterService } from '../../../shared/services/toaster.service';
import { SeoService } from '../../../services/seo.service';
import { SharedService } from '../../../shared/services/shared.service';
import { GolfCourse } from '../../../model/igolf.model';
import { customSettings, User } from '../../../model/user.model';
import { UserSettingService } from '../../../services/user-setting.service';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { FormsModule } from '@angular/forms';
import { DragScrollComponent, DragScrollItemDirective } from 'ngx-drag-scroll';
import { PlayGolfLandingComponent } from '../../play-golf-landing/play-golf-landing.component';
import { AuthService } from '../../../auth/auth.service';
import { getCountryCodeByCountryId } from '../../../utils/country-const';
import { BackButton } from '../../../model/shared.model';

@Component({
    selector: 'app-on-course',
    imports: [CommonModule, TranslatePipe, LottieComponent, FormsModule, DragScrollComponent,
        DragScrollItemDirective, PlayGolfLandingComponent],
    templateUrl: './on-course.component.html',
    styleUrl: './on-course.component.scss',
    providers: [TranslatePipe]
})
export class OnCourseComponent implements OnDestroy {
  courses: GolfCourse[] = [];
  // hasMoreItems: boolean = false;
  noResult: boolean = false;
  pageSize = 15;
  pageNumber = 1;
  loading = false;
  loadingArray = new Array(4);
  isMobileDevice = false;
  userLatitude: number = 0;
  userLongitude: number = 0;
  radius = 100;
  private destroy$ = new Subject<void>();
  private watchId: number | null = null;
  showSetting = false;
  selectedCourse: GolfCourse | null = null;
  customizeObj: customSettings = {
    general: '',
    onCourse: '',
    distance: 'yards',
    onCourseMode: 'social',
    onCourseAudio: 'on',
  };
  user: User | null = null;
  isSettingSaving = false;
  isLocationLoading = false;
  isApple = false;
  isLocationEnabled = false;
  animationOptions: AnimationOptions = {
    path: '/assets/animations/Processing_Black.json',
  };
  isLoggedIn = false;
  courseImagePaths= ['assets/images/courses/course1.jpg', 'assets/images/courses/course2.jpg', 'assets/images/courses/course3.jpg', 'assets/images/courses/course4.jpg', 'assets/images/courses/course5.jpg'];
  prevButtonAction = new BackButton();
  constructor(
    private iGolfSourceService: IGolfSourceService,
    private router: Router,
    private toastr: ToasterService,
    private _sharedService: SharedService,
    private _userSettingService: UserSettingService,
    @Inject(PLATFORM_ID) private platformId: object,
    private _translatePipe: TranslatePipe,
    private seoService: SeoService,
    private _authService: AuthService
  ) {
    effect(() => {
      const buttonAction = this._sharedService.backButtonAction();
      if (buttonAction && buttonAction.action === 'onCourse') {
        this.selectedCourse = null;
        this.showSetting = false;
        setTimeout(() => {
          this._sharedService.backButton.set(this.prevButtonAction);
        }, 100);
      }
    });
  }
  ngOnInit() {
    this.isLoggedIn = this._authService.isSignedIn();
    this.seoService.updateSeo();
    if (isPlatformBrowser(this.platformId)) {
      this.isMobileDevice = this.isMobile();
      if (this.isMobileDevice) {
        this.isLocationLoading = true;
        navigator.geolocation.getCurrentPosition(
          (location) => {
            this.loading = true;
            this.isLocationEnabled = true;
            this.isLocationLoading = false;

            this.userLatitude = location.coords.latitude;
            this.userLongitude = location.coords.longitude;
            const leaveTime = localStorage.getItem('onCourseLeaveTime');
            const onCourseId = localStorage.getItem('onCourseId');
            if (leaveTime && onCourseId) {
              const currentTime = new Date().toISOString();
              const timeDiff = this.calculateTimeDifference(
                currentTime,
                leaveTime
              );
              if (timeDiff > 4) {
                localStorage.removeItem('onCourseLeaveTime');
                localStorage.removeItem('HoleNumber');
                localStorage.removeItem('onCourseId');
                localStorage.removeItem('OnCourseChatId');
                this.getAllCourses();
              } else {
                this.router.navigate(['in-play/' + onCourseId], {
                  state: { hideExceptTopMenu: true },
                });
              }
            } else {
              this.getAllCourses();
            }
          },
          (error) => {
            const ua = navigator.userAgent;
            this.isApple = /iPhone|iPad|iPod/i.test(ua);
            this.isLocationLoading = false;
            this.isLocationEnabled = false;
          },
          {
            enableHighAccuracy: false,
            timeout: 60000,
            maximumAge: 10000, // Use cached location if within 30 seconds
          }
        );
      }
      this.getUserSetting();
    }
  }
  isMobile(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent.toLowerCase()
    );
  }
  getAllCourses() {
    this.loading = true;
    navigator.geolocation.getCurrentPosition(
      (location) => {
        this.userLatitude = location.coords.latitude;
        this.userLongitude = location.coords.longitude;
        // this.hasMoreItems = false;
        this.noResult = false;
        this.iGolfSourceService
          .getAllCourses(
            '',
            this.userLongitude,
            this.userLatitude,
            this.radius,
            this.pageNumber,
            this.pageSize
          )
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (res) => {
              const mappedCourses = res.courseList.map(course => ({
                ...course,
                countryCode: getCountryCodeByCountryId(course.id_country) || '' 
              }));
              this.courses = this.courses.concat(mappedCourses);
              if (this.courses.length == 0) {
                this.noResult = true;
              }
              // if (res.courseList.length == this.pageSize) {
              //   this.hasMoreItems = true;
              // }
            },
          });
      },
      (error) => {
        this.loading = false;
        const ua = navigator.userAgent;
        const isiOS = /iPhone|iPad|iPod/i.test(ua);
        const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
        this.toastr.error(
          'Error fetching location:' + error.message,
          'Location Error'
        );
      },
      {
        enableHighAccuracy: false,
        timeout: 60000,
        maximumAge: 10000, // Use cached location if within 30 seconds
      }
    );
  }
  selectCourse(selectedCourse: GolfCourse) {
    this.selectedCourse = selectedCourse;
  }

  navigateToselectedId() {
    this.isSettingSaving = true;
    this._userSettingService
      .customize(this.customizeObj)
      .pipe(
        finalize(() => {
          this.isSettingSaving = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.user!.customSettings = this.customizeObj;
          this._sharedService.setUser(this.user!);
          this.router.navigate(
            ['in-play/' + this.selectedCourse?.id_course],
            {
              state: { hideExceptTopMenu: true },
            }
          );
        },
      });
  }
  showCaddieSetting(course: GolfCourse) {
    this.selectedCourse = course;
    // if (this.selectedCourse) {
      this.showSetting = true;
      this.prevButtonAction = this._sharedService.backButton();
      this._sharedService.backButton.set({
        show: true,
        url: '/',
        action: 'onCourse',
        text: 'Courses'
      });
      // this.router.navigate(['on-course/' + this.courseId], {
      //   state: { hideExceptTopMenu: true },
      // });
    // } else {
    //   this.toastr.error(
    //     this._translatePipe.transform('StartRoundValidation'),
    //     this._translatePipe.transform('Error')
    //   );
    // }
  }
  loadMoreCourses() {
    this.pageNumber++;
    this.getAllCourses();
  }
  calculateTimeDifference(currentTime: string, leaveTime: string): number {
    // Convert both times to Date objects
    const current = new Date(currentTime);
    const leave = new Date(leaveTime);

    // Get the difference in milliseconds
    const diffInMs = current.getTime() - leave.getTime();

    // Convert milliseconds to hours
    const diffInHours = diffInMs / (1000 * 60 * 60);

    return diffInHours;
  }
  getUserSetting() {
    this._sharedService.user$.subscribe({
      next: (res) => {
        this.user = res;
        if (this.user.customSettings) {
          this.customizeObj = this.user.customSettings;
          if (!this.customizeObj.onCourseAudio) {
            this.customizeObj.onCourseAudio = 'on';
          }
          if (!this.customizeObj.onCourseMode) {
            this.customizeObj.onCourseMode = 'social';
          }
          if (!this.customizeObj.distance) {
            this.customizeObj.distance = 'yards';
          }
        }
      },
    });
  }
  getGreeting(): string {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon';
    } else if (hour >= 17 && hour < 21) {
      return 'Good Evening';
    } else {
      return 'Good Night';
    }
}
get firstName(): string {
  return this.user?.name?.split(' ')[0] || '';
}
  ngOnDestroy() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId); // Stop geolocation tracking
    }
    this.destroy$.next();
    this.destroy$.complete(); // Cleanup all subscriptions
  }
}
